import * as React from 'react';
import { useEffect, useState, useRef, useCallback } from "react";
import { useParams } from 'react-router-dom';
import Apirest from '../../Content/Apirest';
import { useContent } from '../../Content/cms';


import JSZip from 'jszip';
import { saveAs } from 'file-saver';

// Import interfaces
import { FeatureExtractionSummary, Population, FeatureExtractionSummaryProps, SemaphoreProbability } from './FeatureExtraction';
import { ProtocolResData, ProtocolItemResData, FeatureExtractionResData, PopulationDistributionFlat} from '../Protocol/types';

// Import components
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Nav from 'react-bootstrap/Nav';
import Alert from 'react-bootstrap/Alert';
import Button  from 'react-bootstrap/Button';
import Dropdown from 'react-bootstrap/Dropdown';
import WordCloudChart from '../../Commons/Components/Charts/WordCloudChart';
import PrintableReport from './printable_report';

import Section, { SubSection } from "../../Commons/Section";
import { IconButton } from "../../Commons/MultipleComponents";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFlask, faDownload, faComment, faFilePdf, faPlay, faPause } from "@fortawesome/free-solid-svg-icons";
import { RowSpinner } from "../../Commons/MultipleComponents";
import ParticipantHeader from '../../Commons/Components/Participant/Header';
import GoBackButton from '../../Commons/Components/GoBackButton';


import './index.css';
import DomainCompositeResults from './DomainCompositeResults';
import ScreeningResults from './ScreeningResults';

const seconds = 10;


const TriggerWithAudio: React.FC<{trigger: string, triggerAudio?: string}> = ({trigger, triggerAudio}) => {
    const [playing, setPlaying] = React.useState(false);
    const audioRef = React.useRef<HTMLAudioElement | null>(null);

    React.useEffect(() => {
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }
        };
    }, []);

    const togglePlay = () => {
        if (!audioRef.current) {
            audioRef.current = new Audio(triggerAudio);
            audioRef.current.onended = () => setPlaying(false);
        }
        if (playing) {
            audioRef.current.pause();
            setPlaying(false);
        } else {
            if (audioRef.current.ended) {
                audioRef.current.currentTime = 0;
            }
            audioRef.current.play();
            setPlaying(true);
        }
    };

    if (!triggerAudio) {
        return <div className={'protocol-title'} dangerouslySetInnerHTML={{__html: trigger}} />;
    }

    return (
        <div className='trigger-with-audio'>
            <button className='trigger-play-btn' onClick={togglePlay}>
                <FontAwesomeIcon icon={playing ? faPause : faPlay} />
            </button>
            <div dangerouslySetInnerHTML={{__html: trigger}} />
        </div>
    );
};


function sortedProtocolResData(data: ProtocolResData) {
    let ordered_data = { ...data };
    ordered_data.responses = data.responses.sort(
        (a, b) => a.protocol_item.order - b.protocol_item.order
    );
    return ordered_data;
}

interface ProtocolResUrlParams {
    participant_id: string
    protocolres_id: string
}

interface SemaphoreData {
  lambda_function: string;
  bhattacharyya_probabilities: SemaphoreProbability[];
}

export type ProtocolResSemaphoreData = Record<number, SemaphoreData[]>;

const ProtocolRes : React.FC = () => {

    let getContent = useContent();
    const mobiles = require('is-mobile');
    let mobile = mobiles();


    let {participant_id, protocolres_id} = useParams<ProtocolResUrlParams>();

    const fetchedCategories = useRef<Set<string>>(new Set());
    const distributionCache = useRef<Map<string, PopulationDistributionFlat[]>>(new Map());
    let [populationFeatureDistributions, setPopulationFeatureDistributions] = useState<Map<string, PopulationDistributionFlat[]>|undefined>();

    const fetchDistributionsForCategory = useCallback((categoryId: number, languageId: number) => {
        const fetchKey = `${categoryId}-${languageId}`;
        if (fetchedCategories.current.has(fetchKey)) {
            setPopulationFeatureDistributions(new Map(distributionCache.current));
            return;
        }
        fetchedCategories.current.add(fetchKey);
        Apirest.get_feature_distribution(
            { protocol_item_category: categoryId, language: languageId },
            (data: PopulationDistributionFlat[]) => {
                data.forEach((p) => {
                    const key = `${p.protocol_item_category_id}-${p.lambda_function_id}-${p.language_id}`;
                    if (distributionCache.current.has(key)) {
                        distributionCache.current.get(key)!.push(p);
                    } else {
                        distributionCache.current.set(key, [p]);
                    }
                });
                setPopulationFeatureDistributions(new Map(distributionCache.current));
            }
        );
    }, []);

    let [protocolRes, setProtocolResData] = useState<ProtocolResData|undefined>();
    let [protocolItemRes, setProtocolItemRes] = useState<ProtocolItemResData|undefined>();
    let [protocolItemResAudioUrl, setTranscriptionTaskAudioUrl] = useState<string|undefined>();
    let [protocolItemResAudioSrc, setTranscriptionTaskAudioSrc] = useState<string|undefined>();
    let [protocolItemFeatures, setProtocolItemFeatures] = useState<FeatureExtractionSummaryProps|undefined>();
    let [showPrintableReportModal, setShowPrintableReportModal]= useState<boolean>(false);
    let [protocolResSemaphoreData, setProtocolResSemaphoreData] = useState<ProtocolResSemaphoreData>({});

    // DATA HARCODED WHILE DEVELOPING PDF REPORT !!! this should be replace with real data
    let has_protocol_configuration = true

    const setServerData = (data: ProtocolResData) => {

        setProtocolResData(sortedProtocolResData(data));

        // If there is no current protocol item assigned or the one assigned is not in the new server data,
        // Then it set the first protocol item.
        // Else set the updated protocol item data of the current protocol item data
        setProtocolItemRes(prevProtocolItemRes => {

            let prevProtocolItemResExists = (
                prevProtocolItemRes !== undefined &&
                data.responses.find(t => t.id === prevProtocolItemRes.id) !== undefined
            );

            if (prevProtocolItemResExists) {
                return prevProtocolItemRes;
            } else {
                return data.responses[0];
            }
        });
    };

    // Initial on mount trigger
    useEffect(
        () => {
            Apirest.get_protocolres(
                participant_id,
                protocolres_id,
                setServerData
            );

            Apirest.get_protocol_res_bhattacharyya_probabilities(
                protocolres_id,
                setProtocolResSemaphoreData
            );

            // Clean up
            return () => {
                setProtocolResData(undefined);
                setProtocolItemRes(undefined);
                setTranscriptionTaskAudioUrl(undefined);
                setTranscriptionTaskAudioSrc(undefined);
                setProtocolItemFeatures(undefined);
                setShowPrintableReportModal(false);
                setProtocolResSemaphoreData({});
            }
        },
        [protocolres_id] // eslint-disable-line react-hooks/exhaustive-deps
    );

    function keep_pooling() {
        if (protocolRes !== undefined) {

            /* NO responses */
            if (protocolRes.responses.length === 0) {
                // There in no transcription tasks yet
                return true;
            }
            /*  end */

            /* feature extraction tasks not ready */
            if (protocolRes.responses.some(r => r.feature_extraction_res.some(fer => fer.status === 'pending'))) {
                // There are items of the protocol still not transcribed
                return true;
            }
            /*  end */
        }

        return false;
    };

    // pooling of transcription task transcript and features
    // Each time the protocolRes data changes the useEffect is triggered
    // if there is data to pool, then the useWEffect fetches new data in 10 seconds
    // and updates the protocolRes data, then a new useEffect is triggered and
    // keep pooling is called again and if still not all ready then continues pooling.
    // Once keep_pooling returns false, then the useEffect is not triggered anymore
    useEffect(
        () => {
            // if there is at least one transcription task without the transcript or the feature, then ask data again in 10 seconds
            if (keep_pooling()) {

                console.log('%c Protocol data not ready, request transcription task data again in ' + seconds.toString() + ' seconds', 'color: #F945A8');
                setTimeout(
                    () => Apirest.get_protocolres(
                        participant_id,
                        protocolres_id,
                        setProtocolResData
                    ),
                    1000*seconds
                );
            }
        },
        [protocolRes]  // eslint-disable-line react-hooks/exhaustive-deps
    );

    useEffect(() => {
        if (protocolItemRes && protocolRes) {
            const categoryId = protocolItemRes.protocol_item?.category?.id;
            const languageId = protocolRes.protocol?.locale?.lang?.id;
            if (categoryId && languageId) {
                fetchDistributionsForCategory(categoryId, languageId);
            }
        }
    }, [protocolItemRes, protocolRes]); // eslint-disable-line react-hooks/exhaustive-deps

    function findAllScoreValues(protocol_item_id: number,
                                func_entry : string,
                                func_score: string) : SemaphoreProbability[] {

        if (protocolResSemaphoreData[protocol_item_id] === undefined) {
            // If there is no data for the protocol item, return null
            return [];
        }

        // Find the function entry probas
        const item_probas = protocolResSemaphoreData[protocol_item_id];
        let item_lambda_function = item_probas.find(
            item => item.lambda_function === func_entry
        );
        
        if (item_lambda_function === undefined) {
            // If there is no data for the function, return null
            return [];
        }

        // Find all probas with for the given score for different populations
        let probas = item_lambda_function.bhattacharyya_probabilities
            .filter(proba => proba.score === func_score);

        if (probas.length === 0) {
            // If there is no data for the score, return null
            return [];
        }

        return probas;
    }
    
    // Calculate and set the protocol item features to be displayed
    const calculateAndSetProtocolItemFeatures = (currProtocolItemRes: ProtocolItemResData) => {

        let clinical_populations: Population[] = [];

        // Only consider features that the backend marks as visible (default: visible)
        const visibleFeatures = currProtocolItemRes.feature_extraction_res.filter(fer => fer.show !== false);

        if (protocolRes !== undefined && populationFeatureDistributions !== undefined) {

            clinical_populations = visibleFeatures.map(
                (fer: FeatureExtractionResData) => {

                    const categoryId = currProtocolItemRes?.protocol_item?.category?.id;
                    const funcId = fer.lambda_function.id;
                    const languageId = protocolRes?.protocol?.locale?.lang?.id;
                    const key = `${categoryId}-${funcId}-${languageId}`;

                    let populations = populationFeatureDistributions?.get(key);

                    if (populations === undefined) {
                        return [];
                    }

                    return populations.map(
                        (population: PopulationDistributionFlat) => {
                            return {
                                name: population.population.name,
                                feature_name: population.lambda_function_name + '__' + population.score_name,
                                fill_color: population.population.fill_color,
                                stroke_color: population.population.stroke_color,
                                histogram: population.distribution
                            };
                        }
                    );
                }
            ).flat();
        }

        // Warning: it could happen the populations are not the same as the scores, but this is not 
        // a problem, because the scores are shown and populations filter inside each score. 
        // Then, the populations are shown only for those scores that have a population distribution

                let tell_populations: Population[] = visibleFeatures
                    .filter((x: FeatureExtractionResData) => (x.status === 'ready'))
                    .map((fer: FeatureExtractionResData) => {
            return Object.entries(fer.feature_population_distribution.data).map(
                ([score, histogram]: [string, {x: number[], y: number[]}]) => {
                    return ({
                        name: getContent('protocol_result__populations__feature_extraction'),
                        feature_name: fer.lambda_function.name + '__' + score,
                        fill_color: '#5dbd3361',
                        stroke_color: '#5DBD33',
                        histogram: histogram
                    })
                }
            )
        }).flat();

        // concatenate ia_populations and control_populations
        let populations = clinical_populations.flat().concat(...tell_populations);

        let scores = visibleFeatures.map(
            (fer: FeatureExtractionResData) => {
                // Show a single row with the feature name and status if the feature is not ready
                if (fer.status !== 'ready') {
                    return [{
                        name: fer.lambda_function.name,
                        status: fer.status,
                        semaphore_value: []
                    }]
                }
                
                // Show a row for each score of the feature if the feature is ready
                return Object.entries(fer.scores).map(
                    ([score, value]: [string, number]) => {
                        return ({
                            name: fer.lambda_function.name + '__' + score,
                            status: fer.status,
                            value: value,
                            semaphore_value: findAllScoreValues(
                                currProtocolItemRes.protocol_item.id,
                                fer.lambda_function.entry,
                                score
                            )
                        })
                        
                    }
                )
            }
        ).flat();

        
        let sortedScores = scores.sort((a, b) => {
            if (a.semaphore_value.length === 0 && b.semaphore_value.length > 0) return 1;
            if (a.semaphore_value.length > 0 && b.semaphore_value.length === 0) return -1;
            return 0;
        })
        
        setProtocolItemFeatures({
            loadingPopulations: populationFeatureDistributions === undefined,
            data: sortedScores,
            populations: populations
        })
    };

    useEffect(
        () => {
            if (protocolItemRes !== undefined) {
                calculateAndSetProtocolItemFeatures(protocolItemRes);
            }

        }, [protocolItemRes, populationFeatureDistributions, protocolResSemaphoreData] // eslint-disable-line react-hooks/exhaustive-deps
    );

    function getAudioURL() {
        if (protocolRes && protocolItemRes) {
            console.log('######### getAudioURL #########');
            console.log('participant_id:', participant_id !== undefined);
            console.log('protocolRes:', protocolRes !== undefined);
            console.log('protocolItemRes:', protocolItemRes);
            Apirest.get_protocol_res_audio_url_presigned(
                participant_id,
                protocolRes.id.toString(),
                protocolItemRes.protocol_item.id.toString(),
                setTranscriptionTaskAudioUrl
            );
            Apirest.get_protocol_res_audio_src(
                participant_id,
                protocolRes.id.toString(),
                protocolItemRes.protocol_item.id.toString(),
                setTranscriptionTaskAudioSrc
            );

        }
    }

    // get current item audio URL
    useEffect(
        () => getAudioURL(),
        [protocolItemRes] // eslint-disable-line react-hooks/exhaustive-deps
    );

    function downloadAudio() {
        if (protocolRes && protocolItemRes) {
            Apirest.download_protocol_res_audio(
                participant_id.toString(),
                protocolRes?.id.toString(),
                protocolItemRes.protocol_item.id.toString()
            );
        }
    }

    // Show spinner if data not already fetched
    if (!protocolRes || !protocolItemRes || !protocolItemFeatures) {
        return (
            <Container fluid className={'mt-5'}>
                <RowSpinner show />;
            </Container>
        );
    }

    // TODO: move all computations from next block to a useEffect listening protocolItemRes to not be recalculated in every render
    /** ################################# */

    let transcription = protocolItemRes?.feature_extraction_res.find(
        (f: FeatureExtractionResData) => f.lambda_function.entry === 'transcript'
    );

    let wordcloud = protocolItemRes?.feature_extraction_res.find(
        (f: FeatureExtractionResData) => f.lambda_function.entry === 'words-count'
    );

    let all_pending = protocolItemRes?.feature_extraction_res.every(
        fer => fer.status !== 'ready'
    );

    let all_failed = protocolItemRes?.feature_extraction_res.every(
        fer => fer.status === 'failed'
    );

    /** ################################# */
    
    let colums_adjustment = mobile === false ? 6 : 12;

    return (
        <Container fluid>
            <ParticipantHeader />
            <Row className={'bg-info p-2 d-flex justify-content-between align-items-center'}>
                <Col className={'d-flex align-items-center py-1'}>
                <h5 className={'text-white font-weight-bold mb-0 d-flex align-items-center'}>
                    <FontAwesomeIcon icon={faFlask} className={'mr-2'}/>{getContent("mind_lab__title")}
                </h5>
                </Col>
                <GoBackButton/>
            </Row>
            <Row>

                {/* Protocol item nav bar */}
                <Col xs={12} className={'my-0 p-0 '} >
                    <Row className={mobile ? 'd-flex py-3 px-0 ': 'd-flex p-3'}>
                        <Col xs={colums_adjustment}>
                            { mobile ? <h4 className={"font-weight-bold mx-0 px-3 text-primary "}>
                            {getContent("protocolres_prefix_name") + " " + protocolRes.protocol.name}
                        </h4> : <h3 className={"font-weight-bold mx-3 px-3 text-primary"}>
                            {getContent("protocolres_prefix_name") + " " + protocolRes.protocol.name}
                        </h3> }
                        </Col>
                        <Col xs={colums_adjustment} className={'align-bottom'}>
                        <p className={(mobile ? ' px-3 mx-0 small' : 'text-right mb-0 pb-0  px-3 mx-3 mt-2')}>
                        {getContent("protocol_result__date_creation") + ": " + protocolRes.date_created.format('LLL')}
                        </p>
                        </Col>
                        <Col xs={colums_adjustment}>
                        { protocolRes.comment !== "" &&
                        <p className={'mx-3 px-3 p-2 border-bottom'}> <FontAwesomeIcon icon={faComment} color={'#8037c7'}/> <small className={'px-2'}><em>{protocolRes.comment}</em></small></p>
                        }
                        </Col>
                        { has_protocol_configuration &&
                        <Col xs={colums_adjustment} className={'d-flex flex-row-reverse '}>
                        <Button
                            className={'ml-0 mt-1 pr-3 btn-secondary px-3 mx-3'}
                            onClick={() => setShowPrintableReportModal(true)}
                        > <FontAwesomeIcon icon={faFilePdf} className={'mr-2'} />{getContent("printable_report_button")} </Button>
                        </Col>}
                        
                    </Row>
                </Col>

                {/* Ready protocol transcription task */}
                {protocolItemRes &&
                    <Col className={(mobile ? 'bg-light' : 'bg-light p-5')}>
                        {mobile === false ? 
                            <Nav variant="pills" defaultActiveKey={0} className={'text-center align-content-center d-flex'}>
                                {protocolRes?.responses.map((response, idx) =>
                                    <Nav.Item key={idx} className={'m-1'}>
                                        <Nav.Link
                                            eventKey={idx}
                                            onClick={() => setProtocolItemRes(response)}
                                        >
                                            {response.protocol_item.title}
                                        </Nav.Link>
                                    </Nav.Item>
                                )}
                            </Nav> :
                            <Row className={'mt-3'}>
                                <Col sm={12}> 
                                    <Nav>
                                        <Dropdown>
                                        <Dropdown.Toggle id={'protocolResItem'} className={'btn-primary'}>{getContent("protocol_result__dropdown_items_title")}</Dropdown.Toggle>
                                            <Dropdown.Menu className={'protocolResItemsManu'}>
                                            {protocolRes?.responses.map((response, idx) =>
                                                <Dropdown.Item key={idx} onClick={() => setProtocolItemRes(response)}>
                                                    {response.protocol_item.title}
                                                </Dropdown.Item>
                                                )}
                                            </Dropdown.Menu>
                                        </Dropdown>
                                    </Nav>
                                </Col>
                            </Row>
                        }
                        
                        <Section
                            className={'secondary-section'}
                            /*headerClassName={'secondary-section-header'}*/
                            bodyClassName={'secondary-section-body'}
                        >
                            <Row>
                                <Col xs={colums_adjustment} className={'description-section'}>
                                    <SubSection
                                        title={getContent("protocol_result__slogan")}
                                        className={'secondary-subsection mt-3'}
                                        foldable={mobile ? true : false}
                                    >
                                        <TriggerWithAudio
                                            trigger={protocolItemRes.protocol_item.trigger}
                                            triggerAudio={protocolItemRes.protocol_item.trigger_audio}
                                        />
                                        {protocolItemRes.protocol_item.image !== undefined && <div className={'text-center'}>
                                            <img
                                                className={'img-fluid mt-3'}
                                                alt={getContent("protocol_result__title")}
                                                src={protocolItemRes.protocol_item.image}
                                            />
                                        </div>}
                                        {protocolItemRes.protocol_item.audio !== undefined && <div className={'text-center'}>
                                            <audio
                                                className={'mt-4 fluid'}
                                                title={getContent('protocol_result__title')}
                                                src={protocolItemRes.protocol_item.audio}
                                                preload={'auto'}
                                                controls
                                            />
                                        </div>}
                                        {protocolItemRes.protocol_item.video !== undefined && <div className={'text-center'}>
                                            <video
                                                className={'mt-4 img-fluid'}
                                                title={getContent('protocol_result__title')}
                                                src={protocolItemRes.protocol_item.video}
                                                controls
                                            />
                                        </div>}

                                    </SubSection>
                                </Col>
                                {protocolItemRes.is_skipped ?

                                <Col>
                                <Alert variant={'danger'}>
                                        <h3>{getContent("protocol_result__skipped_item")}</h3>
                                        {protocolItemRes.skipped_purpose && 
                                            <p>{protocolItemRes.skipped_purpose}</p>
                                        }
                                </Alert>
                                </Col>
                                
                            :
                                <Col xs={colums_adjustment}>
                                    <SubSection
                                        title={getContent("protocol_result__audio")}
                                        className={'secondary-subsection mt-3'}
                                        foldable={mobile ? true : false}
                                    >
                                        <Container fluid>
                                            <RowSpinner show={!protocolItemResAudioUrl} />
                                            <Row className={'mt-3'}>
                                                <Col xs={10} >
                                                    {protocolItemResAudioUrl && <audio
                                                        className={'w-100'}
                                                        title={protocolItemRes.protocol_item.title}
                                                        src={protocolItemResAudioSrc}
                                                        preload={'auto'}
                                                        controls
                                                    />}
                                                </Col>
                                                <Col xs={2} className={'p-3 text-center'}>
                                                    {<IconButton
                                                        onClick={downloadAudio}
                                                        icon={{icon:faDownload,color:"#8037c7"}}
                                                        text={getContent('protocol_result__feature__download_audio')}
                                                        />
                                                    }
                                                </Col>
                                            </Row>
                                        </Container>
                                    </SubSection>
                                    {/* There is already some summary metrics processed*/}
                                    {transcription &&
                                            <SubSection
                                                title={getContent('protocol_result__transcript_section_title')}
                                                className={'secondary-subsection mt-3 '}
                                                foldable={mobile ? true : false}
                                            >
                                                <div className={'p-2'}>
                                                    {transcription.data.transcript === "" ? <Alert variant={'success'}>
                                                         <p>{getContent("protocol_result__transcript_empty")}</p>
                                                    </Alert> : transcription.data.transcript }
                                                </div>
                                            </SubSection>}

                                    {/** If transcription task is still in progress, then show a spinner */}
                                    {all_pending && <SubSection
                                        title={getContent("protocol_result__transcript_in_progress")}
                                        className={'secondary-subsection mt-3'}
                                    >
                                        <RowSpinner show />
                                    </SubSection>}

                                    {/** Show an alert if there is not transkiption task because it failed */}
                                    {all_failed && <Alert variant={'danger'} className={'mt-3 text-center'}>
                                        {getContent("protocol_result__transcript_failed")}
                                    </Alert>}

                                </Col>}
                            </Row>
                        </Section>

                        {/* Protocol Composite Results */}
                        <DomainCompositeResults protocolResId={protocolres_id} />

                        {/* Screening Results */}
                        <ScreeningResults protocolResId={protocolres_id} />

                        {/* WorldCloud*/}
                        {wordcloud &&
                            <Section
                                title={getContent("protocol_result__wordcloud__title")}
                                className={'secondary-section'}
                                headerClassName={'secondary-section-header'}
                                bodyClassName={'secondary-section-body'}
                            >
                                <Row>
                                    <Col>
                                        <WordCloudChart words={wordcloud.data.words_count} />
                                    </Col>
                                </Row>
                            </Section> 
                        } 

                        {/* There is already some summary metrics processed*/}
                        
                        {protocolItemFeatures &&
                            <Row>
                                { protocolItemRes.is_skipped ?
                                null :
                                <FeatureExtractionSummary
                                    loadingPopulations={protocolItemFeatures.loadingPopulations}
                                    data={protocolItemFeatures.data}
                                    populations={protocolItemFeatures.populations}
                                />}
                            </Row> 
                        } 
                    </Col>
                }
            </Row>
            <PrintableReport
                show={showPrintableReportModal}
                onHide={() => setShowPrintableReportModal(false)}
                protocolRes={protocolRes}
            />
        </Container>
    );
};

export default ProtocolRes;


export async function downloadProtocolRes(getContent: (key: string) => string, participant_id: string, protocolres_id: string, examiner_last_name: any) {
    
    Apirest.get_protocolres(
        participant_id,
        protocolres_id,
        async (data: ProtocolResData) => {

            let zip = new JSZip();

            let protocolName = (
                participant_id + '_' +
                examiner_last_name + '_' +
                data.date_created.format('YYYY-MM-DD:HH:mm:ss')
            );
            
            let all_summary :string[][] = [
                ['protocol item', 'metric', 'value']
            ];

            // Create an array to hold the promises
            let promises: Promise<void>[] = [];

            data.responses.forEach((protocolItemRes: ProtocolItemResData) => {

                let itemName = 'item_' + (protocolItemRes.protocol_item.order+1);
                if (protocolItemRes.protocol_item.title) {
                    itemName = protocolItemRes.protocol_item.title;
                }

                // Create a new folder that I now it will success as item names are different
                let folder = zip.folder(itemName)!;

                promises.push(
                    new Promise<void>((resolve, reject) => {
                        Apirest.get_protocol_res_audio_url_presigned(
                            participant_id,
                            protocolres_id,
                            protocolItemRes.protocol_item.id.toString(),
                            async (presignedUrl: string) => {

                                // Fetch the audio file from the presigned URL
                                const response = await fetch(presignedUrl);
                                const arrayBuffer = await response.arrayBuffer();

                                // Add the audio file to the zip
                                folder.file('response.webm', arrayBuffer, {binary: true});

                                // Resolve the promise
                                resolve();
                            }
                        );
                    })
                );

                folder.file(
                    "trigger.txt",
                    protocolItemRes.protocol_item.trigger
                );

                let transcription = protocolItemRes.feature_extraction_res.find(
                    (f: FeatureExtractionResData) => f.lambda_function.entry === 'transcript'
                );

                folder.file(
                    "transcript.txt",
                    (transcription !== undefined 
                        ? transcription.data.transcript
                        : 'Transcription not present'
                    )
                );

                let summary: string[][] = [['metric', 'value']];
                protocolItemRes.feature_extraction_res.forEach((row: FeatureExtractionResData) => {
                    
                    Object.entries(row.scores).forEach(
                        ([score, value]: [string, number]) => {
                            let name = getContent("protocol_result__feature_extraction__" + row.lambda_function.name + '__' + score + "__name")
                            summary.push([name, value.toString()]);
                            all_summary.push([itemName, name, value.toString()]);
                        }
                    )
                });

                folder.file(
                    "summary.csv",
                    summary.map(row => row.join(';')).join('\n')
                );
            });

            // Wait for all the promises to resolve
            await Promise.all(promises);

            zip.file(
                'summary.csv',
                all_summary.map(row => row.join(';')).join('\n')
            );

            zip.generateAsync({type:"blob"}).then(function(content) {
                saveAs(content, protocolName);
            });
        }
    );
}